import { useMemo, useState } from 'react';
import { CalendarIcon, Loader2, Send, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Field, SelectField, OTHER_CITY } from './CompanyFields';
import { ListEditor } from './ListEditor';
import { STATES } from '@/data/states';
import { OPPORTUNITY_TYPES, CONTACT_METHODS } from '@/data/taxonomy';
import { citiesOfState } from '@/lib/geo';
import { useData } from '@/hooks/useData';
import { opportunityService } from '@/services/api';
import { formatDate } from '@/lib/format';
import { MODERATION_RULES } from '@/lib/moderation';
import { cn } from '@/lib/utils';

const fromOpp = (o, company) => {
  const uf = o?.location.uf || company?.location.uf || '';
  const city = o?.location.city || company?.location.city || '';
  const known = uf ? citiesOfState(uf).includes(city) : true;
  return {
    title: o?.title || '',
    type: o?.type || '',
    categoryId: o?.categoryId || company?.categoryId || '',
    summary: o?.summary || '',
    description: o?.description || '',
    requirements: o?.requirements || [],
    uf,
    city: known ? city : city ? OTHER_CITY : '',
    cityOther: known ? '' : city,
    targetUfs: o?.targetUfs || [],
    deadline: o?.deadline ? new Date(o.deadline) : null,
    contactMethod: o?.contactMethod || 'form',
  };
};

function validate(f) {
  const e = {};
  if (f.title.trim().length < 10) e.title = 'O título precisa ter ao menos 10 caracteres.';
  if (!f.type) e.type = 'Selecione o tipo de oportunidade.';
  if (!f.categoryId) e.categoryId = 'Selecione a categoria.';
  if (f.summary.trim().length < 20) e.summary = 'O resumo precisa ter ao menos 20 caracteres.';
  if (f.description.trim().length < 60) e.description = 'A descrição precisa ter ao menos 60 caracteres.';
  if (!f.uf) e.uf = 'Selecione o estado.';
  if (!f.city) e.city = 'Selecione a cidade.';
  if (f.city === OTHER_CITY && f.cityOther.trim().length < 2) e.cityOther = 'Informe o nome da cidade.';
  if (f.deadline && f.deadline.getTime() < Date.now() - 86400000) e.deadline = 'O prazo não pode estar no passado.';
  return e;
}

export const OpportunityForm = ({ company, opportunity, onSaved }) => {
  const { categories } = useData();
  const [f, setF] = useState(() => fromOpp(opportunity, company));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) =>
    setF((p) => ({
      ...p,
      [k]: v?.target ? v.target.value : v,
      ...(k === 'uf' ? { city: '', cityOther: '' } : {}),
    }));
  const cities = useMemo(() => (f.uf ? citiesOfState(f.uf) : []), [f.uf]);
  const contactOptions = CONTACT_METHODS.filter(
    (m) => m.id === 'form' || (m.id === 'whatsapp' && company?.whatsapp) || (m.id === 'email' && company?.email) || (m.id === 'site' && company?.website)
  );

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error('Revise os campos destacados.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: f.title.trim(),
        type: f.type,
        categoryId: f.categoryId,
        summary: f.summary.trim(),
        description: f.description.trim(),
        requirements: f.requirements,
        location: { uf: f.uf, city: f.city === OTHER_CITY ? f.cityOther.trim() : f.city },
        targetUfs: f.targetUfs,
        deadline: f.deadline ? new Date(new Date(f.deadline).setHours(23, 59, 0, 0)).toISOString() : null,
        contactMethod: f.contactMethod,
        companyId: company.id,
      };
      const saved = opportunity ? await opportunityService.update(opportunity.id, payload) : await opportunityService.create(payload);
      if (saved.status === 'em_analise') {
        toast.warning('Publicação enviada para análise', { description: `A verificação automática encontrou: ${saved.moderationFlags.join(', ')}.` });
      } else {
        toast.success(opportunity ? 'Oportunidade atualizada' : 'Oportunidade publicada', { description: 'Ela já aparece no radar.' });
      }
      onSaved?.(saved);
    } catch (err) {
      toast.error(err.message || 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="opportunity-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="opp-title-input" label="Título" required error={errors.title} className="sm:col-span-2" hint={`${f.title.length}/120`}>
          <Input id="opp-title-input" value={f.title} onChange={set('title')} maxLength={120} aria-invalid={!!errors.title} data-testid="opp-title-input" placeholder="Ex.: Procuramos fornecedores de embalagens no Sul" />
        </Field>
        <Field id="opp-type-select" label="Tipo de oportunidade" required error={errors.type}>
          <SelectField id="opp-type-select" value={f.type} onChange={set('type')} placeholder="Selecione" invalid={!!errors.type} options={OPPORTUNITY_TYPES.map((t) => ({ value: t.id, label: t.name }))} />
        </Field>
        <Field id="opp-category-select" label="Categoria" required error={errors.categoryId}>
          <SelectField id="opp-category-select" value={f.categoryId} onChange={set('categoryId')} placeholder="Selecione" invalid={!!errors.categoryId} options={categories.filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
        <Field id="opp-summary-input" label="Resumo" required error={errors.summary} className="sm:col-span-2" hint="Uma frase que aparece nos cartões.">
          <Input id="opp-summary-input" value={f.summary} onChange={set('summary')} maxLength={200} aria-invalid={!!errors.summary} data-testid="opp-summary-input" />
        </Field>
        <Field id="opp-description-input" label="Descrição detalhada" required error={errors.description} className="sm:col-span-2" hint={`${f.description.length}/2000`}>
          <Textarea id="opp-description-input" rows={6} value={f.description} onChange={set('description')} maxLength={2000} aria-invalid={!!errors.description} data-testid="opp-description-input" />
        </Field>
        <Field id="opp-requirements" label="Requisitos comerciais" className="sm:col-span-2">
          <ListEditor id="opp-requirements" items={f.requirements} onChange={set('requirements')} placeholder="Ex.: Certificação ISO 9001" max={10} testId="opp-requirements" />
        </Field>
        <Field id="opp-state-select" label="Estado" required error={errors.uf}>
          <SelectField id="opp-state-select" value={f.uf} onChange={set('uf')} placeholder="Selecione" invalid={!!errors.uf} options={STATES.map((s) => ({ value: s.uf, label: `${s.name} (${s.uf})` }))} />
        </Field>
        <Field id="opp-city-select" label="Cidade" required error={errors.city} hint={f.uf ? 'Não encontrou sua cidade? Escolha "Outra cidade...".' : 'Selecione o estado primeiro.'}>
          <SelectField
            id="opp-city-select"
            value={f.city}
            onChange={set('city')}
            placeholder={f.uf ? 'Selecione' : '—'}
            invalid={!!errors.city}
            options={[...cities.map((c) => ({ value: c, label: c })), ...(f.uf ? [{ value: OTHER_CITY, label: 'Outra cidade...' }] : [])]}
          />
        </Field>
        {f.city === OTHER_CITY && (
          <Field id="opp-city-other-input" label="Nome da cidade" required error={errors.cityOther} className="sm:col-span-2">
            <Input id="opp-city-other-input" value={f.cityOther} onChange={set('cityOther')} maxLength={60} aria-invalid={!!errors.cityOther} data-testid="opp-city-other-input" />
          </Field>
        )}
        <Field id="opp-deadline" label="Prazo (opcional)" error={errors.deadline}>
          <div className="flex gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="secondary" className={cn('w-full justify-start border border-white/10 font-normal', !f.deadline && 'text-muted-foreground')} data-testid="opp-deadline-button">
                  <CalendarIcon /> {f.deadline ? formatDate(f.deadline.toISOString()) : 'Sem prazo'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto border-white/10 bg-[#0b0f16] p-0" align="start">
                <Calendar mode="single" selected={f.deadline || undefined} onSelect={(d) => set('deadline')(d || null)} disabled={{ before: new Date() }} initialFocus />
              </PopoverContent>
            </Popover>
            {f.deadline && <Button type="button" variant="ghost" onClick={() => set('deadline')(null)}>Remover</Button>}
          </div>
        </Field>
        <Field id="opp-contact-select" label="Forma de contato" hint="Opções dependem dos canais informados no seu perfil.">
          <SelectField id="opp-contact-select" value={f.contactMethod} onChange={set('contactMethod')} options={contactOptions.map((m) => ({ value: m.id, label: m.name }))} />
        </Field>
        <Field id="opp-targets" label="Regiões de interesse (opcional)" className="sm:col-span-2" hint="Aparecem como conexões no mapa.">
          <div className="flex flex-wrap gap-1.5" data-testid="opp-targets">
            {STATES.map((s) => {
              const on = f.targetUfs.includes(s.uf);
              return (
                <button
                  key={s.uf}
                  type="button"
                  aria-pressed={on}
                  onClick={() => set('targetUfs')(on ? f.targetUfs.filter((x) => x !== s.uf) : [...f.targetUfs, s.uf])}
                  title={s.name}
                  className={cn('w-11 rounded-md border py-1 font-mono text-xs transition-colors', on ? 'border-cyan-300/50 bg-cyan-300/15 text-cyan-100' : 'border-white/10 text-muted-foreground hover:border-white/25')}
                  data-testid={`opp-target-${s.uf}`}
                >
                  {s.uf}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-xs text-muted-foreground">
        <div className="mb-1.5 flex items-center gap-2 font-medium text-foreground"><ShieldAlert className="h-4 w-4 text-[#ff6b84]" /> Antes de publicar</div>
        Publicações passam por verificação automática e podem ser denunciadas. Não são permitidos: {MODERATION_RULES.slice(0, 4).map((r) => r.title.toLowerCase()).join(', ')}.
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="submit" disabled={busy} className="btn-glow" data-testid="opp-submit-button">
          {busy ? <Loader2 className="animate-spin" /> : <Send />} {opportunity ? 'Salvar alterações' : 'Publicar oportunidade'}
        </Button>
      </div>
    </form>
  );
};